import { FindUrlResult, findUrlsInText } from "client/Misc/findUrlsInText";
import {
	isNotNil,
	debounce
} from "client/Misc/utils";
import { deepComparison } from "client/Misc/compareUtils";
import { Node } from "prosemirror-model";
import {
	EditorState,
	Plugin,
	PluginView,
	Transaction
} from "prosemirror-state";
import {
	Decoration,
	DecorationSet,
	DecorationSource,
	EditorView
} from "prosemirror-view";

export type LinkifyPluginState = {
	linkPositions: FindUrlResult[];
};

// This plugin defines a new Plugin view (LinkifyPluginView), the view has two lifecycle methods: update and destroy.
// The update is called every time something changes in state, then the entire text is being scanned for urls.
// Since it is relatively heavy operation the scan does not happen on every update, it runs in debounce.
// The destroy method cleans up the debounced callback.

// After urls' positions were found, they are send to the plugin state using meta information in transaction
// The apply method of the plugin's state reads the transaction and sets it in its own state

// The decorations method then converts the url positions from plugin state into decorations

// Decorations are rendered automatically by prosemirror

export function linkifyPlugin(): Plugin<LinkifyPluginState> {
	const plugin: Plugin<LinkifyPluginState> = new Plugin<LinkifyPluginState>({
		view: () => new LinkifyPluginView(plugin),
		state: {
			init: () => {
				return {
					linkPositions: []
				};
			},
			apply: (tr: Transaction, value: LinkifyPluginState): LinkifyPluginState => {
				const linkPositions: FindUrlResult[] | undefined = tr.getMeta(plugin);

				if (!linkPositions) {
					return value;
				}

				if (value.linkPositions === linkPositions) {
					return value;
				}

				return {
					linkPositions
				};
			}
		},
		props: {
			decorations: (state: EditorState): DecorationSource | nil => {
				const pluginState: LinkifyPluginState | undefined = plugin.getState(state);

				if (!pluginState) {
					return DecorationSet.empty;
				}

				return DecorationSet.create(
					state.doc,
					pluginState.linkPositions
						.map(
							position => Decoration.inline(
								position.start,
								position.end,
								{
									nodeName: "a",
									href: position.url,
									rel: "noopener noreferrer",
									target: "_blank"
								}
							)
						)
				);
			},
		}
	});

	return plugin;
}

class LinkifyPluginView implements PluginView {
	private readonly _plugin: Plugin<LinkifyPluginState>;
	private readonly _debouncedIndicateLinkPositions: Required<PluginView>["update"];
	private readonly _cancellation: AbortController;

	constructor(plugin: Plugin<LinkifyPluginState>) {
		this._plugin = plugin;
		this._cancellation = new AbortController();

		this._debouncedIndicateLinkPositions = debounce(
			this.indicateLinkPositions.bind(this),
			150,
			this._cancellation.signal
		);
	}

	public update(view: EditorView, previousState: EditorState): void {
		this._debouncedIndicateLinkPositions(view, previousState);
	}

	public destroy(): void {
		this._cancellation.abort();
	}

	private indicateLinkPositions(view: EditorView, previousState: EditorState): void {
		if (view.isDestroyed) {
			return;
		}

		const {
			state
		} = view;

		let fragmentStart: number = 0;

		const positions: FindUrlResult[] = [];

		for (let i = 0; i < state.doc.childCount; i++) {
			const child: Node = state.doc.child(i);

			if (child.isText && isNotNil(child.text)) {
				positions.push(
					...findUrlsInText(
						child.text,
						{
							shouldMatchOnWww: true,
							globalPositionOffset: fragmentStart
						}
					)
				);
			}

			fragmentStart += child.nodeSize;
		}

		const previousPluginState: LinkifyPluginState | undefined = this._plugin.getState(previousState);

		if (previousPluginState && deepComparison(previousPluginState.linkPositions, positions)) {
			return;
		}

		const tr: Transaction = state.tr;

		tr.setMeta(this._plugin, positions);

		view.dispatch(tr);
	}
}
